/* Native details, ordinary links and Impact's own product form remain the foundations. */
(() => {
  if (!customElements.get('evolved-product-details')) {
    customElements.define('evolved-product-details', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.addEventListener('toggle', (event) => {
          const opened = event.target;
          if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
          const group = opened.getAttribute('name');
          if (!group) return;
          // Fallback for browsers without exclusive <details name> support.
          this.querySelectorAll('details[open]').forEach((other) => {
            if (other !== opened && other.getAttribute('name') === group) other.open = false;
          });
        }, { capture: true, signal });
        this.addEventListener('shopify:block:select', (event) => {
          let element = event.target;
          while (element && element !== this) {
            if (element instanceof HTMLDetailsElement) element.open = true;
            element = element.parentElement;
          }
        }, { signal });
        window.addEventListener('hashchange', () => this.openHash(), { signal });
        this.openHash();
        document.dispatchEvent(new CustomEvent('evolved:details-ready'));
      }
      disconnectedCallback() { this.controller?.abort(); }
      openHash() {
        const id = window.location.hash.slice(1);
        const target = id && document.getElementById(id);
        if (target && this.contains(target)) this.reveal(target, false);
      }
      reveal(target, focus = true) {
        let element = target;
        while (element && element !== this) {
          if (element instanceof HTMLDetailsElement) element.open = true;
          element = element.parentElement;
        }
        requestAnimationFrame(() => {
          target.scrollIntoView({ block: 'start', behavior: focus && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
          if (focus) target.querySelector('summary')?.focus({ preventScroll: true });
        });
      }
    });
  }
  if (!customElements.get('evolved-detail-links')) {
    customElements.define('evolved-detail-links', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const { signal } = this.controller;
        const sync = () => {
          this.querySelectorAll('[data-ev-detail-link]').forEach((link) => {
            link.closest('li').hidden = !document.getElementById(link.hash.slice(1));
          });
          this.hidden = !this.querySelector('li:not([hidden])');
        };
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-ev-detail-link]');
          if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
          const target = document.getElementById(link.hash.slice(1));
          const details = target?.closest('evolved-product-details');
          if (!details?.reveal) return;
          event.preventDefault();
          history.pushState(null, '', link.hash);
          details.reveal(target);
        }, { signal });
        document.addEventListener('evolved:details-ready', sync, { signal });
        document.addEventListener('shopify:section:load', sync, { signal });
        document.addEventListener('shopify:section:unload', () => requestAnimationFrame(sync), { signal });
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, { once: true, signal });
        else sync();
      }
      disconnectedCallback() { this.controller?.abort(); }
    });
  }
})();

if (!customElements.get('evolved-model-groups')) {
  customElements.define('evolved-model-groups', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      queueMicrotask(() => { if (this.isConnected) this.setup(); });
    }
    disconnectedCallback() { this.controller?.abort(); }
    setup() {
      this.querySelector('.ev-model-series')?.remove();
      this.querySelectorAll('.ev-model-step').forEach(node => node.remove());
      this.options = [...this.querySelectorAll('[data-device-group]')];
      const groups = [...new Set(this.options.map(option => option.dataset.deviceGroup))];
      this.addEventListener('change', event => {
        const option = event.target.closest('[data-device-group]');
        const disclosure = this.closest('.ev-choice-disclosure');
        if (!option || !disclosure) return;
        const label = option.querySelector('[data-device-name]');
        const selectedLabel = disclosure.querySelector('[data-ev-choice-selected]');
        const fullName = label?.dataset.fullName || label?.textContent || '';
        const formatted = selectedLabel.querySelector('evolved-option-label');
        if (formatted) formatted.setAttribute('display-value', fullName); else selectedLabel.textContent = fullName;
        disclosure.open = false;
        disclosure.querySelector('summary').focus({preventScroll:true});
      }, {signal: this.controller.signal});
      if (groups.length < 2) return;
      const iphone = groups.every(group => group.startsWith('iPhone '));
      const galaxy = groups.every(group => group.startsWith('Galaxy S'));
      // Series follow their first occurrence in Shopify's option values.
      const nav = document.createElement('div');
      nav.className = 'ev-model-series';
      nav.setAttribute('role', 'group');
      nav.setAttribute('aria-label', 'Choose series');
      const modelList = this.querySelector('[data-option-selector]');
      groups.forEach(group => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = this.classList.contains('ev-inline-models') && (iphone || galaxy) ? group.replace(/^iPhone |^Galaxy /, '') : group;
        button.setAttribute('aria-label', group);
        if (modelList?.id) button.setAttribute('aria-controls', modelList.id);
        button.dataset.group = group;
        button.setAttribute('aria-pressed', 'false');
        nav.append(button);
      });
      if (this.classList.contains('ev-inline-models') && groups.length > 4) {
        nav.dataset.collapsible = '';
        nav.id = modelList.id + '-series';
        const more = document.createElement('button');
        more.type = 'button'; more.className = 'ev-series-toggle';
        more.textContent = 'More +';
        more.setAttribute('aria-label', 'Show all series');
        more.setAttribute('aria-expanded', 'false');
        more.setAttribute('aria-controls', nav.id);
        more.addEventListener('click', () => {
          const expanded = nav.classList.toggle('is-expanded');
          more.setAttribute('aria-expanded', String(expanded));
          more.setAttribute('aria-label', expanded ? 'Show fewer series' : 'Show all series');
          more.textContent = expanded ? 'Less −' : 'More +';
        }, {signal: this.controller.signal});
        nav.append(more);
      }
      const seriesLabel = document.createElement('p');
      seriesLabel.className = 'ev-model-step';
      seriesLabel.textContent = iphone ? '1. Choose iPhone series' : galaxy ? '1. Choose Galaxy series' : '1. Choose series';
      const modelLabel = document.createElement('p');
      modelLabel.className = 'ev-model-step'; modelLabel.textContent = '2. Choose model';
      this.prepend(seriesLabel, nav, modelLabel);
      this.options.forEach(option => {
        const label = option.querySelector('[data-device-name]');
        if (!label) return;
        const full = label.dataset.fullName || label.textContent.trim();
        label.dataset.fullName = full; option.setAttribute('aria-label', full);
        option.querySelector('input')?.setAttribute('aria-label', full);
        const group = option.dataset.deviceGroup;
        const normalized = full.replace(/^Samsung /, 'Galaxy ');
        const short = normalized.startsWith(group) ? normalized.slice(group.length).trim() : full;
        const display = full === 'iPhone Duo' ? 'Duo' : short === 'e' ? full.replace(/^iPhone /, '') : short === '+' ? 'Plus' : short || 'Standard';
        const formatted = label.querySelector('evolved-option-label');
        if (formatted) formatted.setAttribute('display-value', display); else label.textContent = display;
      });
      this.classList.add('is-enhanced');
      const selected = this.options.find(option => option.querySelector('input:checked') || option.classList.contains('is-selected'));
      this.selectGroup(this.dataset.activeGroup && groups.includes(this.dataset.activeGroup) ? this.dataset.activeGroup : selected?.dataset.deviceGroup || groups[0]);
      nav.addEventListener('click', event => {
        const button = event.target.closest('button[data-group]');
        if (button) this.selectGroup(button.dataset.group);
      }, {signal: this.controller.signal});
      this.addEventListener('change', event => {
        const option = event.target.closest('[data-device-group]');
        if (option) this.selectGroup(option.dataset.deviceGroup);
      }, {signal: this.controller.signal});
      this.closest('x-popover')?.addEventListener('dialog:after-hide', () => {
        const current = this.options.find(option => option.querySelector('input:checked') || option.classList.contains('is-selected'));
        if (current) this.selectGroup(current.dataset.deviceGroup);
      }, {signal: this.controller.signal});
    }
    selectGroup(group) {
      this.dataset.activeGroup = group;
      const modelHeading = this.querySelectorAll('.ev-model-step')[1];
      if (modelHeading) modelHeading.textContent = '2. Choose model · ' + group;
      this.options.forEach(option => { option.hidden = option.dataset.deviceGroup !== group; });
      const buttons = [...this.querySelectorAll('.ev-model-series button[data-group]')];
      const priority = buttons.slice(0, 4);
      const active = buttons.find(button => button.dataset.group === group);
      // Keep the first four in Shopify order; never promote the selected series.
      buttons.forEach(button => {
        button.setAttribute('aria-pressed', String(button === active));
        button.toggleAttribute('data-priority', priority.includes(button));
      });
    }
  });
}
if (!customElements.get('evolved-upsell-visibility')) {
  customElements.define('evolved-upsell-visibility', class extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('[data-ev-card="upsell"]');
      if (!this.card) return;
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.card, {childList: true, subtree: true, attributes: true, attributeFilter: ['hidden','style','class']});
      this.schedule();
    }
    disconnectedCallback() { this.observer?.disconnect(); cancelAnimationFrame(this.frame); }
    schedule() { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.update()); }
    update() {
      if (!this.card?.isConnected) return;
      const visible = [...this.card.querySelectorAll('.axe-main')].some(row => {
        if (!row.querySelector('.axe-display-message')?.textContent.trim()) return false;
        for (let node = row; node && node !== this.card; node = node.parentElement) {
          const style = getComputedStyle(node);
          if (node.hidden || style.display === 'none' || style.visibility === 'hidden') return false;
        }
        return true;
      });
      if (this.card.hidden === visible) this.card.hidden = !visible;
    }
  });
}

// Keep the highest-priority signals on one line as the actual card width changes.
if (!customElements.get('evolved-signal-fit')) {
  customElements.define('evolved-signal-fit', class extends HTMLElement {
    connectedCallback() {
      this.observer = new ResizeObserver(() => this.fit());
      this.observer.observe(this); queueMicrotask(() => this.fit());
      document.fonts?.ready.then(() => { if (this.isConnected) this.fit(); });
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    fit() {
      const list = this.querySelector('.ev-signals'); if (!list) return;
      const items = [...list.children]; items.forEach(item => { item.hidden = false; });
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const availableWidth = list.clientWidth;
      const widths = items.map(item => item.getBoundingClientRect().width);
      let used = 0;
      items.forEach((item, i) => { used += widths[i] + (i ? gap : 0); item.hidden = used > availableWidth; });
    }
  });
}

if (!customElements.get('evolved-purchase-polish')) {
  customElements.define('evolved-purchase-polish', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.ev-pdp-info');
      if (!this.root) return;
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.root, {childList:true,subtree:true});
      this.schedule();
    }
    disconnectedCallback() { this.observer?.disconnect(); cancelAnimationFrame(this.frame); }
    schedule() { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(() => this.update()); }
    update() {
      const stock = this.root.querySelector('evolved-stock-status');
      const buttons = this.root.querySelector('buy-buttons');
      if (stock && buttons && buttons.nextElementSibling !== stock) buttons.after(stock);
      this.root.querySelectorAll('.rapi-bundles__bar').forEach(bar => {
        const badge = bar.querySelector('.rapi-bundles__bar-most-popular');
        const left = bar.querySelector('.rapi-bundles__bar-content-left');
        const subtitle = bar.querySelector('.rapi-bundles__bar-subtitle');
        if (!badge || !left) return;
        let secondary = left.querySelector('.ev-bundle-secondary');
        if (!secondary) { secondary = document.createElement('div'); secondary.className = 'ev-bundle-secondary'; left.append(secondary); }
        if (badge.parentElement !== secondary) secondary.prepend(badge);
        if (subtitle && subtitle.parentElement !== secondary) secondary.append(subtitle);
        const text = badge.querySelector('.rapi-bundles__bar-most-popular__content span');
        if (text && /^(MOST POPULAR|BEST VALUE)$/.test(text.textContent.trim())) text.textContent = text.textContent.trim() === 'MOST POPULAR' ? 'Most popular' : 'Best value';
      });
      this.root.querySelectorAll('.axe-main').forEach(row => {
        const titleElement = row.querySelector('.axe-display-message');
        if (!titleElement) return;
        const visibleTitle = titleElement.textContent.trim();
        if (!titleElement.dataset.evOriginalTitle || visibleTitle !== titleElement.dataset.evRenderedTitle) titleElement.dataset.evOriginalTitle = visibleTitle;
        const title = titleElement.dataset.evOriginalTitle;
        const sub = row.querySelector('.axe-subtitle');
        if (!sub || row.querySelector('.evolved-glass-types')) return;
        const source = sub.dataset.evOriginal || sub.textContent.trim();
        if (!sub.dataset.evOriginal) sub.dataset.evOriginal = source;
        const text = title + ' ' + source;
        const power = text.match(/\b(\d{2,3})\s?W\b/i)?.[1];
        const length = text.match(/\b(\d+(?:\.\d+)?)\s?m\b/i)?.[1];
        const lengthLabel = length ? (window.EvolvedMeasurements?.format(length + ' m') || length + ' m') : ''; 
        let copy = '';
        if (/powercable|magcable/i.test(title)) copy = 'Charge faster';
        else if (/powerdrive/i.test(title)) copy = 'Charge on the go';
        else if (/poweradapter|powerplug/i.test(title)) copy = /required/i.test(source) ? 'Required charging adapter' : 'Compact everyday power';
        else if (/magring/i.test(title)) copy = 'Add magnetic attachment';
        else if (/travel bag/i.test(title)) copy = 'Pack your tech together';
        else if (/ultraglass/i.test(title)) copy = /back/i.test(title) ? 'Rear glass protection' : /privacy/i.test(title) ? 'Screen protection + privacy' : 'Everyday screen protection';
        if (!copy) return;
        let cleanTitle = title.replace(/PowerAdapter/gi, 'PowerPlug');
        if (power) cleanTitle = cleanTitle.replace(new RegExp('\\b' + power + '\\s?W\\b', 'ig'), '');
        if (length) cleanTitle = cleanTitle.replace(new RegExp('\\b' + length.replace('.', '\\.') + '\\s?m\\b', 'ig'), '');
        cleanTitle = cleanTitle.replace(/\s{2,}/g, ' ').replace(/\s*[·—–-]\s*$/, '').trim();
        titleElement.dataset.evRenderedTitle = cleanTitle;
        if (titleElement.textContent.trim() !== cleanTitle) titleElement.textContent = cleanTitle;
        const signature = [title,copy,power,lengthLabel].join('|');
        if (sub.dataset.evSignature === signature) return;
        sub.dataset.evSignature = signature;
        const fragment = document.createDocumentFragment();
        [power && power+'W',lengthLabel].filter(Boolean).forEach(label => {
          const pill = document.createElement('span');pill.className='ev-upsell-spec';pill.textContent=label;fragment.append(pill);
        });
        const label=document.createElement('span');label.className='ev-upsell-benefit';label.textContent=copy;fragment.append(label);
        sub.replaceChildren(fragment);sub.classList.add('ev-upsell-copy');
      });
    }
  });
}

// Presentation-only grouping. Each swatch remains the original Shopify option value.
if (!customElements.get('evolved-finish-groups')) {
  customElements.define('evolved-finish-groups', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.controller = new AbortController();
      queueMicrotask(() => { if (this.isConnected) this.setup(); });
    }
    disconnectedCallback() { this.controller?.abort(); }
    setup() {
      const signal = this.controller.signal;
      this.querySelector('.ev-finish-nav')?.remove();
      this.options = [...this.querySelectorAll('.ev-finish-option')].filter(option => option.querySelector('label, a'));
      const finishes = [...new Set(this.options.map(option => option.dataset.finish))];
      const nav = document.createElement('div'); nav.className = 'ev-finish-nav'; nav.setAttribute('role','group'); nav.setAttribute('aria-label','Titanium finish');
      finishes.forEach(finish => {
        const button = document.createElement('button'); button.type = 'button'; button.dataset.finish = finish;
        button.textContent = finish === 'Black Titanium' ? 'Black titanium' : finish;
        nav.append(button);
      });
      const chosen = this.options.find(option => option.querySelector('input:checked, a.is-selected'));
      this.prepend(nav);
      this.selectFinish(chosen?.dataset.finish || finishes[0]);
      nav.addEventListener('click', event => { const button = event.target.closest('button'); if (button) this.selectFinish(button.dataset.finish); }, {signal});
      this.addEventListener('change', event => {
        const option = event.target.closest('.ev-finish-option');
        const disclosure = this.closest('.ev-choice-disclosure');
        if (!option || !disclosure) return;
        disclosure.querySelector('[data-ev-choice-selected]').textContent = (option.dataset.finish === 'Black Titanium' ? 'Black titanium' : option.dataset.finish) + ' · ' + option.dataset.bandColor;
        disclosure.open = false; disclosure.querySelector('summary').focus({preventScroll:true});
      }, {signal});
    }
    selectFinish(finish) {
      this.querySelectorAll('.ev-finish-nav button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.finish === finish)));
      this.options.forEach(option => { option.hidden = option.dataset.finish !== finish; });
      this.querySelector('evolved-color-options')?.refresh?.();
    }
  });
}

/* Display-only formatting: native option IDs, names, URLs, and fulfillment stay intact. */
if (!customElements.get('evolved-option-label')) {
  customElements.define('evolved-option-label', class extends HTMLElement {
    static get observedAttributes() { return ['display-value']; }
    connectedCallback() { this.format(); }
    attributeChangedCallback() { if (this.isConnected) this.format(); }
    format() {
      const full = this.getAttribute('display-value') || this.dataset.fullValue || this.textContent.trim();
      if (!this.dataset.fullValue) this.dataset.fullValue = full;
      const name = this.dataset.optionName || '';
      const watch = /watch/i.test(name) && /^\d+\s*mm\s*\(/i.test(full);
      const separated = full.split(/\s+[—–]\s+/);
      const detailed = watch || separated.length > 1 || /\d+(?:\.\d+)?[″"]/.test(full);
      if (!detailed) { this.textContent = full.replace(/\s*->\s*/g, ' to '); this.classList.remove('ev-option-parts'); return; }
      let primary = separated.shift(), detail = separated.join(' · '), size = '';
      const badges = [];
      if (name === 'Size & Charging') {
        primary = primary.trim() + ' laptops';
        detail = 'Charges ' + detail.replace(/\bWatch\b/, 'Apple Watch').replace(/\s*\+\s*/g, ', ');
      } else if (watch) {
        const match = full.match(/^(\d+)\s*mm\s*\((.*)\)$/i);
        primary = match[1] + ' mm'; detail = match[2]
          .replace(/(?<!Apple Watch )\bUltra\b/g, 'Apple Watch Ultra')
          .replace(/(?<!Apple Watch )\bSeries\b/g, 'Apple Watch Series')
          .replace(/(?<!Apple Watch )\bSE\b/g, 'Apple Watch SE');
      } else {
        const match = primary.match(/\d+(?:\.\d+)?\s*[″"](?:\s*\/\s*\d+(?:\.\d+)?\s*[″"])?/);
        if (match) {
          size = match[0].replace(/[″"]/g, '″').replace(/\s*\/\s*/g, ' / ');
          primary = primary.replace(match[0], '').trim();
          // When the selected series is already shown above, the screen size is the primary choice.
          if (!primary) { primary = size; size = ''; }
        }
      }
      const years = detail.match(/\(\s*((?:19|20)\d{2}(?:\s*[–-]\s*(?:19|20)\d{2})?)\s*\)/g) || [];
      years.forEach(year => { detail = detail.replace(year, '').trim(); });
      if (size) badges.push({text:size, kind:'size'});
      if (detail) {
        const parts = detail.replace(/\s*\/\s*/g, ' / ').replace(/\s+/g,' ').trim();
        badges.push({text:parts, kind:watch ? 'series' : /\bM\d/.test(parts) ? 'chip' : 'detail'});
      }
      years.forEach(year => badges.push({text:year.replace(/[()]/g,''), kind:'year'}));
      const title = document.createElement('span'); title.className = 'ev-option-parts__name'; title.textContent = primary;
      const meta = document.createElement('span'); meta.className = 'ev-option-parts__meta';
      badges.forEach(badge => {
        const item = document.createElement('span'); item.className = 'ev-option-part ev-option-part--' + badge.kind;
        item.textContent = badge.text; meta.append(item);
      });
      this.classList.add('ev-option-parts'); this.replaceChildren(title, meta);
    }
  });
}
if (!customElements.get('evolved-color-options')) {
  customElements.define('evolved-color-options', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.controller = new AbortController();
      queueMicrotask(() => { if (this.isConnected) this.setup(); });
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.frame); }
    setup() {
      this.list = this.querySelector('.variant-picker__option-values');
      if (!this.list) return;
      this.querySelector('.ev-colors-more')?.remove();
      this.options = [...this.list.querySelectorAll(':scope > .ev-color-option')].filter(option => option.querySelector('label,a'));
      // Keep Shopify's rendered order and availability; never rank or move swatches.
      this.toggle = document.createElement('button'); this.toggle.type = 'button'; this.toggle.className = 'ev-colors-more';
      const input = this.list.querySelector('input[id]');
      this.list.id ||= (input?.id || 'ev-color-options') + '-colors';
      this.toggle.setAttribute('aria-controls', this.list.id); this.toggle.setAttribute('aria-expanded','false');
      this.toggle.textContent = 'More +'; this.list.append(this.toggle);
      this.toggle.addEventListener('click', () => {
        this.expanded = !this.expanded; this.refresh(); this.toggle.focus({preventScroll:true});
      }, {signal:this.controller.signal});
      this.addEventListener('change', () => { this.expanded = false; this.refresh(); }, {signal:this.controller.signal});
      this.observer?.disconnect();
      this.observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width && Math.abs(width - (this.lastWidth || 0)) > 1) { this.lastWidth = width; this.refresh(); }
      });
      this.observer.observe(this);
      this.refresh();
    }
    refresh() {
      if (!this.list || !this.toggle) return;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.layout());
    }
    layout() {
      const available = this.list.clientWidth;
      if (!available) return;
      this.options.forEach(option => option.removeAttribute('data-color-overflow'));
      const options = this.options.filter(option => !option.hidden);
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 6;
      const widths = new Map(options.map(option => [option, option.getBoundingClientRect().width]));
      const total = options.reduce((sum,option) => sum + widths.get(option), 0) + Math.max(0,options.length-1) * gap;
      this.toggle.hidden = total <= available;
      this.toggle.setAttribute('aria-expanded', String(!!this.expanded));
      if (this.toggle.hidden) return;
      if (this.expanded) { this.toggle.textContent = 'Less −'; this.toggle.setAttribute('aria-label','Show fewer colors'); return; }
      this.toggle.textContent = 'More +';
      const reserve = Math.max(72,this.toggle.getBoundingClientRect().width) + gap;
      let budget = available - reserve;
      const visible = [];
      for (const option of options) {
        const required = widths.get(option) + (visible.length ? gap : 0);
        if (required > budget) break;
        visible.push(option); budget -= required;
      }
      // Collapsed colors are a contiguous prefix of Shopify's available options.
      if (!visible.length && options[0]) visible.push(options[0]);
      options.forEach(option => option.toggleAttribute('data-color-overflow', !visible.includes(option)));
      const count = options.length - visible.length;
      this.toggle.textContent = '+' + count + ' more'; this.toggle.setAttribute('aria-label', 'Show ' + count + ' more colors');
    }
  });
}

